import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import {
  NV_PATHS,
  NvAiAgentIcon,
  NvButton,
  NvFilterChip,
  NvFinderResultCard,
  NvLucideChevronIcon,
  NvSearchIcon,
  NvSmallXIcon,
  nvAgentDetailPath,
} from "@/components/navigator";
import { cn } from "@/lib/utils";
import type { FinderCard, FinderFacet, FinderRelaxSuggestion } from "@/services/navigator/finder-service";

// F01 Agent Finder 조각 (Figma ver.7: 847:49745 결과 · 847:49982 비회원 · 847:50233 결과 없음 · 847:50418 가이드형 · 847:57694 전체 목록형 · 847:51770 · 847:52187 더보기 · 접기)

// ── 필터 ───────────────────────────────────────────────────────────────

export function FilterRow({
  label,
  facets,
  selected,
  onToggle,
  showCounts = false,
}: {
  label: string;
  facets: FinderFacet[];
  selected: string[];
  onToggle: (code: string) => void;
  // 칩 라벨 뒤 회색 개수 — 전체 목록형(Figma 847:57694)에서만 보여요. 검색 결과 · 가이드형 시안에는 개수가 없어요
  showCounts?: boolean;
}) {
  return (
    <div className="flex gap-4">
      {/* 시안: 라벨 윗선이 칩 윗선과 같아요(칩 가운데 정렬이 아님) */}
      <span className="w-11 shrink-0 text-[13px] font-bold leading-4 text-nv-text-secondary">{label}</span>
      <div className="flex flex-1 flex-wrap gap-2">
        {facets.map((f) => {
          const on = selected.includes(f.code);
          return (
            <NvFilterChip
              key={f.code}
              selected={on}
              // 선택값별 결과 건수 (IA 55). 0건은 고른 게 아니면 잠가요
              disabled={!on && f.count === 0}
              onClick={() => onToggle(f.code)}
            >
              {f.label}
              {showCounts && <span className="ml-1 font-num text-[12px] font-normal text-nv-text-secondary">{f.count}</span>}
            </NvFilterChip>
          );
        })}
      </div>
    </div>
  );
}

export interface SelectedChip {
  key: string;
  label: string;
  onRemove: () => void;
}

export function SelectedBar({ chips, onReset }: { chips: SelectedChip[]; onReset: () => void }) {
  if (!chips.length) return null;
  return (
    <div className="flex min-h-11 items-center gap-2 rounded-nv-card bg-nv-bg-page px-4 py-[10px]">
      <span className="shrink-0 text-[12px] font-bold leading-[14px] text-nv-text-secondary">선택한 조건</span>
      <div className="flex flex-1 flex-wrap gap-[6px]">
        {chips.map((chip) => (
          <button
            key={chip.key}
            type="button"
            onClick={chip.onRemove}
            aria-label={`${chip.label} 조건 해제`}
            className="inline-flex h-6 items-center gap-[6px] rounded-nv-pill border border-nv-brand-border bg-nv-brand-subtle px-[10px] text-[12px] font-bold leading-[14px] text-nv-text-primary"
          >
            {chip.label}
            <NvSmallXIcon size={12} />
          </button>
        ))}
      </div>
      <button type="button" onClick={onReset} className="shrink-0 text-[12px] font-bold leading-[14px] text-nv-text-primary">
        ↺ 전체 초기화
      </button>
    </div>
  );
}

// ── AI 해석 (김현빈님 해석 결과를 그려요, IA 54 · Figma ver.7 "AI 해석" 띠, 10/13 제안 09) ─────────────
// 직접 고른 필터(선택한 조건)와 줄을 나눠요: 회색 띠 안에 "AI 해석" + ✦ 칩(유형 · 이름 · ×).

export function InterpretationBar({
  items,
  onRemove,
  notice,
}: {
  items: { key: string; type: string; label: string }[];
  onRemove: (key: string) => void;
  // 해석 칩 대신 보여 줄 안내(대체 검색 중일 때)
  notice?: string;
}) {
  if (!items.length && !notice) return null;
  return (
    <div className="flex min-h-[46px] flex-wrap items-center gap-2 rounded-nv-card bg-nv-bg-page px-4 py-[10px]">
      <span className="shrink-0 text-[12px] font-bold leading-[14px] text-nv-text-secondary">AI 해석</span>
      {notice ? (
        <p className="inline-flex items-center gap-2 text-[12px] font-bold leading-[14px] text-nv-text-secondary">
          <NvAiAgentIcon size={14} />
          {notice}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-[6px]">
          {items.map((item) => (
            <span
              key={item.key}
              className="inline-flex h-[26px] items-center gap-[6px] rounded-nv-pill border border-[#F7B48F] bg-nv-brand-subtle pb-[5px] pl-3 pr-[10px] pt-[5px]"
            >
              <span aria-hidden className="text-[12px] font-bold leading-[14px] text-nv-brand">
                ✦
              </span>
              <span className="text-[11px] leading-[13px] text-nv-text-secondary">{item.type}</span>
              <span className="text-[12px] font-bold leading-[14px] text-nv-text-primary">{item.label}</span>
              <button
                type="button"
                onClick={() => onRemove(item.key)}
                aria-label={`${item.label} 해석 제거`}
                className="inline-flex text-nv-text-primary hover:opacity-70"
              >
                <NvSmallXIcon size={13} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 정렬 ───────────────────────────────────────────────────────────────

export function SortMenu<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => !ref.current?.contains(event.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  const current = options.find((o) => o.value === value) ?? options[0];
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="text-[12px] leading-[14px] text-nv-text-secondary hover:text-nv-text-primary"
      >
        {current.label} ▾
      </button>
      {open && (
        <ul role="listbox" className="absolute right-0 top-6 z-20 min-w-[112px] rounded-nv-chip border border-nv-border bg-nv-surface py-1 shadow-sm">
          {options.map((o) => (
            <li key={o.value}>
              <button
                type="button"
                role="option"
                aria-selected={o.value === value}
                onClick={() => {
                  setOpen(false);
                  onChange(o.value);
                }}
                className={cn(
                  "w-full px-3 py-2 text-left text-[12px] leading-[14px] hover:bg-nv-bg-page",
                  o.value === value ? "font-bold text-nv-text-primary" : "text-nv-text-secondary",
                )}
              >
                {o.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── 카드 (Figma ver.7 "Card / Agent Result (제안)", 10/13 제안 09 · 13) ──────────────────

// △ Blur 카드: 서버는 이름 · 공급사 · 로고 · Certified만 주니 나머지 줄은 고정 문구로 채워 흐리게 그려요
const LOCKED_SUMMARY = "에이전트 소개와 대표 검증 지표, 산업 · 업무 경로는 로그인 후 확인할 수 있어요. 이용 권한이 있는 회원에게만 보여요.";

export function ResultCard({
  card,
  onBookmark,
}: {
  card: FinderCard;
  onBookmark?: (card: FinderCard, next: boolean) => void;
}) {
  if (card.locked) {
    return (
      <NvFinderResultCard
        name={card.name}
        supplier={card.supplier_name}
        summary={LOCKED_SUMMARY}
        logoUrl={card.logo_url ?? undefined}
        toneKey={card.id}
        certified={card.is_certified}
        tags={["단일 에이전트", "Plug & Play"]}
        category="산업 · 업무 경로"
        metric={{ label: "대표 검증 지표", value: "00%" }}
        bookmarked={false}
        locked
      />
    );
  }
  return (
    <NvFinderResultCard
      name={card.name}
      supplier={card.supplier_name}
      summary={card.summary ?? ""}
      logoUrl={card.logo_url ?? undefined}
      toneKey={card.id}
      certified={card.is_certified}
      tags={(card.tags ?? []).map((t) => ({ label: t.label, definition: t.definition }))}
      conceptual={card.is_conceptual}
      topMatch={card.is_top_match}
      category={card.category ?? undefined}
      metric={card.metric ?? undefined}
      bookmarked={card.is_bookmarked ?? false}
      onBookmarkToggle={(next) => onBookmark?.(card, next)}
      to={nvAgentDetailPath(card.id)}
      linkState={{ source: "finder" }}
    />
  );
}

// 결과 없음 (Figma ver.7 847:50233, 10/13 제안 15): 안내 + "이 조건을 빼면 결과가 있어요" 칩 + [조건 초기화] · [AI Assistant 상담]
export function EmptyResult({
  onReset,
  assistantPath,
  suggestions = [],
  isAiCondition,
  onRelax,
}: {
  onReset: () => void;
  assistantPath: string;
  suggestions?: FinderRelaxSuggestion[];
  // 해석으로 자동 선택한 조건이면 칩 앞에 ✦
  isAiCondition?: (suggestion: FinderRelaxSuggestion) => boolean;
  onRelax?: (suggestion: FinderRelaxSuggestion) => void;
}) {
  return (
    <div className="flex flex-col items-center gap-[14px] rounded-nv-card border border-nv-border bg-nv-surface px-10 py-14">
      <span aria-hidden className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-nv-bg-page text-nv-text-primary">
        <NvSearchIcon size={32} />
      </span>
      <p className="text-[20px] font-medium leading-6 text-nv-text-primary">조건에 맞는 에이전트를 찾지 못했습니다</p>
      <p className="text-[14px] leading-[17px] text-nv-text-secondary">검색어를 바꾸거나 선택한 조건을 줄여 다시 검색해 보세요.</p>
      {suggestions.length > 0 && (
        <div className="flex flex-col items-center gap-[10px]">
          <p className="text-[13px] font-medium leading-4 text-[#5E616B]">이 조건을 빼면 결과가 있어요</p>
          <div className="flex flex-wrap justify-center gap-2">
            {suggestions.map((s) => (
              <button
                key={`${s.kind}:${s.code ?? ""}`}
                type="button"
                onClick={() => onRelax?.(s)}
                className="inline-flex h-[34px] items-center gap-[6px] rounded-[18px] border border-[#DEE0E5] bg-nv-surface px-[14px] text-[13px] leading-4 transition-colors hover:border-nv-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand"
              >
                <span className="font-medium text-nv-text-primary">
                  {isAiCondition?.(s) && <span aria-hidden>✦ </span>}
                  {s.label} 빼기
                </span>
                <span className="font-num font-bold text-nv-brand">→ {s.count.toLocaleString("ko-KR")}개</span>
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="flex items-center gap-2">
        <NvButton variant="secondary" onClick={onReset}>
          조건 초기화
        </NvButton>
        <NvButton
          variant="ghost"
          className="h-[46px] text-nv-text-primary underline decoration-nv-brand decoration-[1.5px] underline-offset-[5px] hover:bg-transparent"
          asChild
        >
          <Link to={assistantPath}>AI Assistant 상담</Link>
        </NvButton>
      </div>
    </div>
  );
}

export function SectionTitle({
  title,
  count,
  note,
  className,
  noteClassName,
}: {
  title: string;
  count?: number;
  note?: string;
  className?: string;
  noteClassName?: string;
}) {
  return (
    <div className={cn("flex items-end gap-[10px]", className ?? "pt-[18px]")}>
      <h2 className="text-[18px] font-medium leading-[22px] text-nv-text-primary">
        {title}
        {count !== undefined && <span className="ml-2 font-num">{count}</span>}
      </h2>
      {note && <span className={noteClassName ?? "pb-[2px] text-[12px] leading-[14px] text-nv-text-secondary"}>{note}</span>}
    </div>
  );
}

// ── 추천 더보기 · 접기 (slide 33 · 35, Figma ver.7 847:49745 · 847:52187) ─────────────

// 처음 10개 + [더보기]마다 10개씩. 버튼에 (노출 수 / 전체), 마지막 묶음이 나오면 [접기]로 바뀌고 누르면 처음 10개로 돌아가요
export function MoreButton({
  shown,
  total,
  onMore,
  onCollapse,
  disabled,
  label = "추천 에이전트",
}: {
  shown: number;
  total: number;
  onMore?: () => void;
  onCollapse?: () => void;
  disabled?: boolean;
  // "추천 에이전트 더보기 (10 / 37)" · 전체 목록형은 "전체 에이전트 더보기 (10 / 652)"
  label?: string;
}) {
  const expandedAll = shown >= total;
  return (
    <div className="flex justify-center">
      <NvButton
        variant="secondary"
        size="sm"
        onClick={expandedAll ? onCollapse : onMore}
        disabled={disabled}
        className="h-[38px] gap-[6px] rounded-nv-chip px-5 text-[13px] font-medium leading-4 text-nv-text-primary"
      >
        {expandedAll ? (
          <>
            접기
            <NvLucideChevronIcon direction="up" />
          </>
        ) : (
          <>
            {label} 더보기 ({shown} / {total.toLocaleString("ko-KR")})
            <NvLucideChevronIcon />
          </>
        )}
      </NvButton>
    </div>
  );
}

// ── 초기 진입 가이드 (검색어 · 필터 없음, Figma 546:4397) ───────────────────

export const FINDER_PLACEHOLDER = "필요한 에이전트를 자연어로 검색해 보세요 (예: 계약서 조항을 자동으로 검토해 주는 에이전트)";

// 예시 문장과 산업 · 업무 태그는 시안 샘플 그대로예요(설계서에 이 화면 정의가 없어요 — 확정되면 이 상수만 바꿔요)
export const EXAMPLE_SEARCHES: { text: string; tags: { kind: "산업" | "업무"; label: string }[] }[] = [
  { text: "설비 점검 리포트와 센서 로그로 고장을 미리 알려주는 에이전트", tags: [{ kind: "산업", label: "산업재·제조" }, { kind: "업무", label: "운영" }] },
  { text: "계약서 독소조항을 찾아서 검토 의견을 정리해 주는 에이전트", tags: [{ kind: "산업", label: "금융" }, { kind: "업무", label: "리스크·보안" }] },
  { text: "판매 데이터로 다음 달 재고 수요를 예측하는 에이전트", tags: [{ kind: "산업", label: "소비재·유통" }, { kind: "업무", label: "SCM" }] },
];

// AI 해석 줄 자리에 나오는 "추천 검색어" (HOME과 같은 API)
export function SuggestedTerms({ terms, onPick }: { terms: string[]; onPick: (term: string) => void }) {
  if (!terms.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-[63px] shrink-0 text-[13px] font-medium leading-4 text-nv-text-secondary">추천 검색어</span>
      {terms.map((term) => (
        <button
          key={term}
          type="button"
          onClick={() => onPick(term)}
          className="inline-flex items-center whitespace-nowrap rounded-nv-chip border border-nv-border bg-nv-surface px-3 py-[6px] text-nv-tag text-nv-text-primary transition-colors hover:border-nv-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nv-brand"
        >
          {term}
        </button>
      ))}
    </div>
  );
}

export function GuideStart({ onSearch }: { onSearch: (q: string) => void }) {
  return (
    <div className="flex flex-col gap-9 pt-[14px]">
      <p className="flex items-center gap-[10px] rounded-nv-card bg-nv-brand-subtle px-5 py-[15px] text-[13px] leading-4 text-nv-text-primary">
        <NvAiAgentIcon size={16} className="shrink-0" />
        검색어를 입력하거나 위 필터를 선택하면 조건에 맞는 에이전트를 추천해 드려요.
      </p>

      <section className="flex flex-col gap-[14px]">
        <SectionTitle
          title="이렇게 검색해 보세요"
          note="상황을 구체적으로 적을수록 정확해요"
          className=""
          noteClassName="pb-[3px] text-[13px] leading-4 text-nv-text-placeholder"
        />
        <div className="grid gap-3 md:grid-cols-3">
          {EXAMPLE_SEARCHES.map((example) => (
            <div key={example.text} className="flex flex-col gap-[14px] rounded-nv-card border border-nv-border bg-nv-surface p-[18px]">
              <p className="break-keep text-[14px] font-medium leading-[21px] text-nv-text-primary">“{example.text}”</p>
              <div className="flex flex-wrap gap-[6px]">
                {example.tags.map((tag) => (
                  <span key={tag.kind} className="inline-flex h-[19px] items-center gap-[5.5px] rounded-nv-pill bg-nv-bg-subtle px-2 text-[11px] leading-[13px]">
                    <span className="text-nv-text-secondary">{tag.kind}</span>
                    <span className="font-bold text-nv-text-primary">{tag.label}</span>
                  </span>
                ))}
              </div>
              <button
                type="button"
                onClick={() => onSearch(example.text)}
                className="self-start text-[12px] font-bold leading-[14px] text-nv-text-primary underline decoration-nv-brand-border decoration-[1.5px] underline-offset-[3px]"
              >
                이 문장으로 검색 →
              </button>
            </div>
          ))}
        </div>
      </section>

      <div className="flex items-center justify-between gap-4 rounded-nv-card bg-nv-brand-subtle px-6 py-5">
        <div className="flex flex-col gap-1">
          <p className="text-[15px] font-medium leading-[18px] text-nv-text-primary">다른 기업은 어떻게 도입했는지 궁금하다면</p>
          <p className="text-[13px] leading-4 text-nv-text-secondary">Case Study에서 산업별 에이전트 도입 사례와 성과를 확인해 보세요</p>
        </div>
        <NvButton asChild size="sm">
          <Link to={NV_PATHS.caseStudy}>Case Study 보기</Link>
        </NvButton>
      </div>
    </div>
  );
}
